/**
 * Sales coding (docs/features/order-to-invoice/50_finance_sales_ledger_module_spec.md
 * §4.9): which Xero account each kind of CIS sales line posts to. The Ledger
 * rules page mounts it beside the purchase ledger's Coding tab, in the same
 * pattern (doc 70 Q7: no new design).
 *
 * The keys are a fixed set (items, each service kind, every other service),
 * each with its current rule or none: a due line with no rule holds its
 * invoice, so a missing key is worth seeing. A change writes a new version
 * with a reason; invoices already issued keep their codes. Every word is the
 * sales ledger's.
 */
import { useCallback, useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { SalesCodingRuleSlot } from "@shared-core/data/sales-ledger-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { Skeleton } from "../../primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { Textarea } from "../../primitives/textarea";

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; slots: SalesCodingRuleSlot[] };

function SetAccountDialog({
  slot,
  onClose,
  onSaved,
}: {
  slot: SalesCodingRuleSlot;
  onClose: () => void;
  onSaved: () => void;
}) {
  const adapter = useDataAdapter();
  const id = useId();
  const [code, setCode] = useState(slot.rule?.xero_account_code ?? "");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setErrors({});
    try {
      await adapter.writeSalesCodingRule({
        line_kind: slot.line_kind,
        service_kind: slot.service_kind,
        xero_account_code: code.trim(),
        reason: reason.trim(),
      });
      toast.success(`${slot.label} now posts to ${code.trim()}`);
      onSaved();
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length)
        setErrors(
          Object.fromEntries(fieldErrors.map((e) => [e.field, e.message])),
        );
      else
        setErrors({
          form: err instanceof Error ? err.message : "Could not save the rule.",
        });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{slot.label}: Xero account</DialogTitle>
          <DialogDescription>
            Invoices raised from now on post these lines to this account.
            Invoices already issued keep theirs.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          {errors.form && (
            <p role="alert" className="text-sm text-red-600">
              {errors.form}
            </p>
          )}
          <div className="space-y-1">
            <Label htmlFor={`${id}-code`}>Xero account code</Label>
            <Input
              id={`${id}-code`}
              value={code}
              placeholder="4000"
              aria-invalid={errors.xero_account_code ? true : undefined}
              aria-describedby={
                errors.xero_account_code ? `${id}-code-error` : undefined
              }
              onChange={(e) => setCode(e.target.value)}
            />
            {errors.xero_account_code && (
              <p id={`${id}-code-error`} className="text-sm text-red-600">
                {errors.xero_account_code}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${id}-reason`}>Reason</Label>
            <Textarea
              id={`${id}-reason`}
              rows={2}
              value={reason}
              aria-invalid={errors.reason ? true : undefined}
              aria-describedby={
                errors.reason ? `${id}-reason-error` : undefined
              }
              onChange={(e) => setReason(e.target.value)}
            />
            {errors.reason && (
              <p id={`${id}-reason-error`} className="text-sm text-red-600">
                {errors.reason}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SalesCodingRulesTab() {
  const adapter = useDataAdapter();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [editing, setEditing] = useState<SalesCodingRuleSlot | null>(null);
  const load = useCallback(() => {
    adapter
      .listSalesCodingRules()
      .then((slots) => setState({ kind: "loaded", slots }))
      .catch((err: unknown) =>
        setState({
          kind: "failed",
          message:
            err instanceof Error ? err.message : "Could not load the rules.",
        }),
      );
  }, [adapter]);
  useEffect(load, [load]);

  if (state.kind === "loading")
    return <Skeleton className="h-40 w-full" aria-label="Loading rules" />;
  if (state.kind === "failed")
    return (
      <p role="alert" className="text-sm text-red-600">
        {state.message}
      </p>
    );
  const missing = state.slots.filter((s) => !s.rule).length;
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground max-w-2xl text-sm">
        Which Xero account each kind of sales line in One posts to. A line due
        to be invoiced with no rule will hold the invoice until a rule is set.
        {missing > 0 && ` ${missing} not set yet.`}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Sales line</TableHead>
            <TableHead>Xero account</TableHead>
            <TableHead>Set</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>
              <span className="sr-only">Change</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {state.slots.map((slot) => (
            <TableRow key={`${slot.line_kind}:${slot.service_kind ?? "all"}`}>
              <TableCell className="font-medium">{slot.label}</TableCell>
              <TableCell className="tabular-nums">
                {slot.rule?.xero_account_code ?? (
                  <span className="text-amber-700 dark:text-amber-400">
                    Not set
                  </span>
                )}
              </TableCell>
              <TableCell>
                {slot.rule ? formatDateOrDash(slot.rule.effective_from) : "—"}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {slot.rule?.reason ?? "—"}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(slot)}
                  aria-label={`Set the Xero account for ${slot.label}`}
                >
                  {slot.rule ? "Change" : "Set"}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {editing && (
        <SetAccountDialog
          slot={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
